// File: /opt/stacks/workout/app/src/hooks/useWakeLock.js
//
// Acquires a Screen Wake Lock while a workout timer is active, preventing
// the device screen from turning off mid-workout.
// Reacquires after the page becomes visible again (wake lock is auto-released
// when the page is hidden).

import { useEffect, useRef, useCallback, useState } from "react";

/**
 * @param {boolean} isActive - true when a workout timer is running
 * @param {boolean} enabled - user setting for wake lock (default true)
 * @returns {{ isLocked: boolean }}
 */
const useWakeLock = (isActive, enabled = true) => {
  const lockRef = useRef(null);
  const [isLocked, setIsLocked] = useState(false);

  const acquire = useCallback(async () => {
    if (!("wakeLock" in navigator)) return;
    if (lockRef.current) return; // already held
    try {
      lockRef.current = await navigator.wakeLock.request("screen");
      setIsLocked(true);
      // Clear ref when browser auto-releases (tab hidden, low battery, etc.)
      lockRef.current.addEventListener("release", () => {
        lockRef.current = null;
        setIsLocked(false);
      });
    } catch {
      // Permission denied or API unavailable — fail silently
      setIsLocked(false);
    }
  }, []);

  const release = useCallback(async () => {
    if (lockRef.current) {
      try {
        await lockRef.current.release();
      } catch {
        // ignore errors on release
      }
      lockRef.current = null;
      setIsLocked(false);
    }
  }, []);

  // Acquire or release based on isActive + enabled setting
  useEffect(() => {
    if (isActive && enabled) {
      acquire();
    } else {
      release();
    }
    return () => {
      release();
    };
  }, [isActive, enabled, acquire, release]);

  // Reacquire after page regains visibility (wake lock is lost when hidden)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && isActive && enabled) {
        acquire();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isActive, enabled, acquire]);

  return { isLocked };
};

export default useWakeLock;
